import classNames from 'classnames';
import * as React from 'react';
import type { StudyPageLinkListProps } from '../../Components/StudyPageLinkList/StudyPageLinkList';
import { StudyPageLinkList } from '../../Components/StudyPageLinkList/StudyPageLinkList';
import { PageSection } from '~/Features/Layouts/Components/PageSection/PageSection';
import { PageSectionHeading } from '~/Features/Layouts/Components/PageSectionHeading/PageSectionHeading';
import type { ArticleCardProps } from '~/Features/StudyPage/Campus/Components/ArticleList/ArticleCard/ArticleCard';
import { ArticleCard } from '~/Features/StudyPage/Campus/Components/ArticleList/ArticleCard/ArticleCard';
import type { OverlayContainerProps } from '~/Features/StudyPage/Campus/Components/Overlay/WithOverlay';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import styles from './JobOpportunitiesSection.module.css';
import type { JobOpportunitiesSectionVariant } from './JobOpportunitiesSectionVariant';

export interface JobOpportunitiesSectionProps {
    id: string;
    studyPageLinkList: Pick<StudyPageLinkListProps, 'teaser' | 'items'>;
    overlayCloseLabel: string;
    heading?: HtmlString;
    overlayButtonLabel?: string;
    overlayHeading?: string;
    overlayContent?: HtmlString;
    featuredArticle?: Pick<ArticleCardProps, 'articleUrl' | 'title' | 'description' | 'image'>;
    variant?: JobOpportunitiesSectionVariant;
}

export const JobOpportunitiesSection: React.FC<JobOpportunitiesSectionProps> = props => {
    const {
        id,
        heading,
        studyPageLinkList,
        featuredArticle,
        overlayButtonLabel,
        overlayContent,
        overlayHeading,
        overlayCloseLabel,
        variant = 'default',
    } = props;

    const hasFeaturedArticle = featuredArticle && !!featuredArticle.articleUrl;

    const overlayProps: OverlayContainerProps<HtmlString> | undefined =
        overlayHeading && overlayContent && overlayButtonLabel && overlayCloseLabel
            ? {
                  overlayLabel: overlayHeading,
                  content: overlayContent,
                  openLabel: overlayButtonLabel,
                  closeLabel: overlayCloseLabel,
              }
            : undefined;

    return (
        <PageSection
            id={id}
            className={classNames(
                styles.JobOpportunitiesSection,
                variant !== 'default' && styles[`Variant--${variant}`],
                hasFeaturedArticle && styles['Variant--withFeaturedArticle']
            )}
        >
            {heading && <PageSectionHeading heading={heading} className={styles.Heading} />}
            <div className={styles.Content}>
                <div className={styles.LinkList}>
                    <StudyPageLinkList {...studyPageLinkList} overlay={overlayProps} variant={variant} />
                </div>
                {hasFeaturedArticle && (
                    <div className={styles.ArticleCard}>
                        <ArticleCard {...featuredArticle} layout="vertical" pictureRatio="16x9" infoLayout="full" />
                    </div>
                )}
            </div>
        </PageSection>
    );
};
